import type { BillingProduct, ConversationMessage, Insight, MemoryItem, Relationship, RelationshipActivity, Topic } from '@mira/clients-shared';

export const sampleTopics: Topic[] = [
  {
    id: 'communication',
    title: 'Communication',
    category_label: 'Relationships',
    summary: 'How I express myself and how I want to be heard.',
    insight_count: 3,
    last_activity_at: '2026-07-18T09:41:00.000Z',
  },
  {
    id: 'family-boundaries',
    title: 'Boundaries with family',
    category_label: 'Family',
    summary: 'Setting limits while staying connected in a healthy way.',
    insight_count: 2,
    last_activity_at: '2026-07-17T20:15:00.000Z',
  },
  {
    id: 'choosing-what-to-say',
    title: 'Choosing what to say',
    category_label: 'Relationships',
    summary: 'Speaking honestly while keeping relationships safe.',
    insight_count: 2,
    last_activity_at: '2026-05-12T18:30:00.000Z',
  },
  {
    id: 'work-boundaries',
    title: 'Work boundaries',
    category_label: 'Work',
    summary: 'Protecting focus and rest without disconnecting from the team.',
    insight_count: 2,
    last_activity_at: '2026-07-12T18:00:00.000Z',
  },
  {
    id: 'self-trust',
    title: 'Self trust',
    category_label: 'Self',
    summary: 'Learning when to pause, when to choose, and what feels true.',
    insight_count: 1,
    last_activity_at: '2026-07-10T08:00:00.000Z',
  },
];

export const sampleInsights: Insight[] = [
  { id: 'insight-1', title: 'Name the need earlier', content: 'You soften conflict when you name what you need before the moment gets crowded.', tags: ['relationship'] },
  { id: 'insight-2', title: 'Leave space after the first answer', content: 'Emma seems to open more when there is a little room after the first response.', tags: ['timing'] },
  { id: 'insight-3', title: 'Rest is productive', content: 'You create your best work after slow mornings and real breaks.', tags: ['Wellbeing'] },
  { id: 'insight-4', title: 'You value depth over breadth', content: 'You feel most fulfilled in a few meaningful relationships, not many.', tags: ['Relationships'] },
  { id: 'insight-5', title: 'Mornings set your tone', content: 'When you start the day with intention, everything flows better.', tags: ['Habits'] },
  { id: 'insight-6', title: 'Clarity comes from space', content: 'Stepping away helps you make decisions with more ease and confidence.', tags: ['Growth'] },
];

export const sampleRelationships: Relationship[] = [
  {
    id: 'emma',
    display_name: 'Emma Chen',
    relationship_text: 'Partner',
    gender: 'female',
    relationship_context: 'We navigate big decisions together and check in about our day.',
  },
  {
    id: 'sarah',
    display_name: 'Sarah Liu',
    relationship_text: 'Sister',
    gender: 'female',
    relationship_context: 'We support each other through life transitions and family matters.',
  },
  {
    id: 'marcus',
    display_name: 'Marcus Reed',
    relationship_text: 'Teammate',
    gender: 'male',
    relationship_context: 'We collaborate on projects and share feedback openly.',
  },
  { id: 'alex-best-friend', display_name: 'Alex', relationship_text: 'Close friend', gender: 'male', relationship_context: 'Trust has grown through honest conversations.' },
  { id: 'mom', display_name: 'Mom', relationship_text: 'Family', gender: 'female', relationship_context: 'Boundaries and care both matter in this relationship.' },
];

export const sampleActivities: RelationshipActivity[] = [
  { id: 'activity-1', relationship_id: 'emma', activity_kind: 'conversation', content: 'Choosing what to say' },
  { id: 'activity-2', relationship_id: 'emma', activity_kind: 'conversation', content: 'Planning time together' },
];

export const sampleMemories: MemoryItem[] = [
  { id: 'memory-1', title: 'You prefer time to think before replying.', content: 'Captured from recent conversations', status: 'pending' },
  { id: 'memory-2', title: 'Evening walks help you reset.', content: 'Captured on May 6, 2025', status: 'confirmed' },
  { id: 'memory-3', title: 'You value direct but gentle conversations.', content: 'Confirmed from repeated reflections', status: 'confirmed' },
];

export const sampleNotes = [
  { id: 'dream-future', title: 'Dream about the future', body: 'Reflections on the life I want to build, the impact I hope to have, and the...', date: 'May 18, 2025', icon: '☼' },
  { id: 'book-ideas', title: 'Book ideas', body: "A list of book ideas, themes I'm curious about, and stories I want...", date: 'May 12, 2025', icon: '□' },
  { id: 'travel-inspiration', title: 'Travel inspiration', body: 'Places that call to me, experiences I want to have, and cultures to...', date: 'May 5, 2025', icon: '◎' },
];

export const sampleMessages: ConversationMessage[] = [
  { id: 'message-1', role: 'assistant', content: 'What feels most alive in this situation today?' },
  { id: 'message-2', role: 'user', content: 'I want to talk about Emma, but I do not want to overreact.' },
  { id: 'message-3', role: 'assistant', content: 'Let us separate what happened from what it touched in you.' },
];

export const sampleProducts: BillingProduct[] = [
  {
    productKey: 'mira-plus-monthly',
    displayName: 'Mira Plus Monthly',
    description: 'Unlimited conversations, smarter memory, and richer relationships.',
    priceLabel: '$7.99 / month',
    features: ['Unlimited conversations', 'Smarter memory', 'Richer relationships'],
  },
  {
    productKey: 'mira-plus-annual',
    displayName: 'Mira Plus Annual',
    description: 'Save with an annual plan.',
    priceLabel: '$59.99 / year',
    features: ['Unlimited conversations', 'Smarter memory', 'Richer relationships'],
  },
];
